import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Keyboard } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { CommonActions } from '@react-navigation/native';
import { format, isSameDay, parseISO } from 'date-fns';
import getSymbolFromCurrency from 'currency-symbol-map';
import { AppView, rem, SCREEN_BOTTOM_PADDING, Spacer } from 'design-system';
import Text from 'design-system/Text';
import Button from 'design-system/Button';
import Indicator from 'design-system/Indicator';
import InfoCard from 'design-system/InfoCard';
import StatusBar from 'design-system/StatusBar';
import { TextButton } from 'design-system/TextButton';
import { SettingsItem, SettingsItemMoneyInput, SettingsItemRightContent, } from 'design-system/SettingsItem';
import { SettingsSection } from 'design-system/SettingsSection';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { IconArrow } from 'design-system/icons/IconArrow';
import { IconBoldCalendar } from 'design-system/icons/IconBoldCalendar';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import Modal from 'utils/packages/Modal';
import { formatAmount, formatDayMonth } from 'utils/formatting';
import createStyleSheets from 'utils/createStyleSheets';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import { IconCoins } from 'design-system/icons';
import { useMarginBottom } from 'hooks/useMarginBottom';
import SelectDateModalContent from '../components/modal/SelectDateModalContent';
import { isWeb } from '../../../constants';
import { editSubscription, getSubscriptionsById } from '../actions';
import { makeSelectSubscriptionById } from '../selectors';
/** isSameDay that tolerates either side being unset. */
const isSameOptionalDay = (a, b) => a && b ? isSameDay(a, b) : a === b;
const strings = {
    title: 'Next payment',
    amount: 'Amount',
    dueDate: 'Due date',
    placeholder: 'Set amount...',
    save: 'Save',
    setDatePlaceholder: 'Set date...',
    revertAll: 'Revert all to predicted',
    revertAmount: 'Revert amount to predicted',
    revertDate: 'Revert date to predicted',
    info: "This counts towards your Committed Spending and updates your True Balance. Changing this won't affect future payment detections.",
    confirmTitle: 'Update payment prediction?',
    addConfirmTitle: 'Add payment prediction?',
    confirmBody: 'This change will affect your Committed spending in Budgeting and the True balance of the account used for this payment',
    cancel: 'Cancel',
    yesUpdate: 'Yes, update',
};
const AdjustPredictionScreen = ({ navigation, route, }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: strings.title,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const { id, isCreate } = route.params;
    useEffect(() => {
        if (id) {
            dispatch(getSubscriptionsById(id));
        }
    }, [dispatch, id]);
    const selectSubscriptionById = useMemo(makeSelectSubscriptionById, []);
    const subscription = useAppSelector((store) => selectSubscriptionById(store, id));
    if (!subscription) {
        return (<AppView isInTab={isWeb} avoidHeader>
        <StatusBar barStyle="dark-content" animated/>
        <Indicator brand/>
      </AppView>);
    }
    return (<AdjustPredictionContent navigation={navigation} subscription={subscription} isCreate={isCreate}/>);
};
const AdjustPredictionContent = ({ navigation, subscription, isCreate, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const amountInputRef = useRef(null);
    const [initialSubscription] = useState(subscription);
    const initialAmount = isCreate
        ? ''
        : initialSubscription.nativePrice.amount.toFixed(2);
    const [amountText, setAmountText] = useState(initialAmount);
    const amount = amountText === '' ? '' : Number(amountText).toFixed(2);
    const predictedDate = useMemo(() => initialSubscription.predictions?.[0]?.date
        ? parseISO(initialSubscription.predictions[0].date)
        : undefined, [initialSubscription.predictions]);
    const [initialDate] = useState(() => isCreate ? undefined : predictedDate ?? new Date());
    const [date, setDate] = useState(initialDate);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const goBackOrToSubscription = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        }
        else {
            navigation.dispatch(CommonActions.reset({
                index: 0,
                routes: [{ name: 'Subscription', params: { id: subscription.id } }],
            }));
        }
    }, [navigation, subscription.id]);
    const amountChanged = amount !== initialAmount;
    const dateChanged = !isSameOptionalDay(date, initialDate);
    const hasChanges = amountChanged || dateChanged;
    const { userPrediction, autoPrediction } = initialSubscription;
    const hasUserPrediction = userPrediction?.date != null || userPrediction?.amount != null;
    const autoDate = useMemo(() => (autoPrediction?.date ? parseISO(autoPrediction.date) : undefined), [autoPrediction?.date]);
    const revertsToAutoPrediction = !isCreate && hasUserPrediction && !!autoDate;
    const targetAmountValue = revertsToAutoPrediction && autoPrediction
        ? autoPrediction.amount
        : initialSubscription.nativePrice.amount;
    const targetAmount = isCreate ? '' : targetAmountValue.toFixed(2);
    const targetDate = revertsToAutoPrediction ? autoDate : initialDate;
    const hintDate = revertsToAutoPrediction ? autoDate : predictedDate;
    const hintAmount = formatAmount(targetAmountValue, initialSubscription.nativePrice.currency, 2);
    const amountDiffersFromTarget = amount !== targetAmount;
    const dateDiffersFromTarget = !isSameOptionalDay(date, targetDate);
    const showAmountHint = !isCreate && amountDiffersFromTarget;
    const showDateHint = !isCreate && !!hintDate && !isSameOptionalDay(date, hintDate);
    const showRevert = !isCreate &&
        !isFetching &&
        (amountDiffersFromTarget || dateDiffersFromTarget);
    const revertAmountOnly = amountDiffersFromTarget && !dateDiffersFromTarget;
    const revertDateOnly = dateDiffersFromTarget && !amountDiffersFromTarget;
    let revertText = strings.revertAll;
    if (revertAmountOnly) {
        revertText = strings.revertAmount;
    }
    else if (revertDateOnly) {
        revertText = strings.revertDate;
    }
    const onChangeAmount = useCallback((_formatted, extracted = '') => {
        setAmountText(extracted);
    }, []);
    const onClearAmount = useCallback(() => {
        setAmountText('');
    }, []);
    const revertAmount = useCallback(() => {
        setAmountText(targetAmount);
        setTimeout(() => Keyboard.dismiss(), 100);
    }, [targetAmount]);
    const revertAll = useCallback(() => {
        setAmountText(targetAmount);
        setDate(targetDate);
    }, [targetAmount, targetDate]);
    const openDatePicker = useCallback(() => {
        Keyboard.dismiss();
        Modal.show(<SelectDateModalContent data={date ?? new Date()} predictedDate={hintDate} closeOnRevert selectExtraDays disablePastDates onFinish={(selected) => {
                setDate(selected);
                Modal.hide();
            }}/>, {
            title: isWeb ? 'Select a date' : undefined,
        });
    }, [date, hintDate]);
    const clearsUserPrediction = revertsToAutoPrediction &&
        !amountDiffersFromTarget &&
        !dateDiffersFromTarget;
    const submitUpdate = useCallback(() => {
        Modal.hide();
        // When creating, the picker starts empty so any date is one the user chose.
        // Otherwise it falls back to today when there is no prediction — only
        // persist that date if they actually changed it.
        const sendDate = isCreate
            ? !!date
            : predictedDate !== undefined || dateChanged;
        fetch(editSubscription(subscription.id, clearsUserPrediction
            ? { userPredictedAmount: null, userPredictedDate: null }
            : {
                userPredictedAmount: amount,
                userPredictedDate: sendDate && date ? format(date, 'yyyy-MM-dd') : undefined,
                isActive: true,
            }))
            .then(() => fetch(getSubscriptionsById(subscription.id)))
            .then(() => goBackOrToSubscription());
    }, [
        amount,
        date,
        dateChanged,
        isCreate,
        predictedDate,
        clearsUserPrediction,
        fetch,
        goBackOrToSubscription,
        subscription.id,
    ]);
    const onPressSave = useCallback(() => {
        if (!hasChanges) {
            goBackOrToSubscription();
            return;
        }
        Modal.showConfirmation({
            title: isCreate ? strings.addConfirmTitle : strings.confirmTitle,
            subtitle: strings.confirmBody,
            confirmButtonTitle: strings.cancel,
            cancelButtonTitle: strings.yesUpdate,
            onConfirm: () => Modal.hide(),
            onPressCancel: submitUpdate,
        });
    }, [goBackOrToSubscription, hasChanges, isCreate, submitUpdate]);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, styles.scrollContent);
    return (<AppView isInTab={isWeb} avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <KeyboardAwareScrollView contentContainerStyle={contentContainerStyle} keyboardShouldPersistTaps="handled" mode="layout" bottomOffset={SCREEN_BOTTOM_PADDING}>
        <SettingsSection noMarginTop>
          <SettingsItemMoneyInput ref={amountInputRef} description={strings.amount} icon={<IconCoins gradientKey="darkPink"/>} prefix={getSymbolFromCurrency(subscription.nativePrice.currency)} value={amountText} useNumbersFont={!!amountText} showClearOnZero onChangeText={onChangeAmount} onClearTextInput={onClearAmount} placeholder={strings.placeholder} maximumIntegerDigits={9} customSubDescription={showAmountHint ? (<Text TextThin-14 Primary>
                  Predicted:{' '}
                  <Text NumbersThin-14 Primary>
                    {hintAmount}
                  </Text>
                </Text>) : undefined}/>
          <SettingsItem topDescription={strings.dueDate} title={date ? formatDayMonth(date) : strings.setDatePlaceholder} titleProps={date ? undefined : { color: colors.text.placeholder }} description={showDateHint && hintDate
            ? `Predicted: ${formatDayMonth(hintDate)}`
            : undefined} descriptionProps={{ Primary: true }} onPress={openDatePicker} icon={<IconBoldCalendar gradientKey="darkPurple"/>}>
            <SettingsItemRightContent>
              <IconArrow direction="down"/>
            </SettingsItemRightContent>
          </SettingsItem>
        </SettingsSection>
        <Spacer.H16 />
        <InfoCard>
          <Text TextThin-14 Secondary>
            {strings.info}
          </Text>
        </InfoCard>
      </KeyboardAwareScrollView>
      <KeyboardAvoidingContainerNew sticky withFrame={false} isInTab={isWeb}>
        {showRevert ? (<TextButton text={revertText} onPress={revertAmountOnly ? revertAmount : revertAll} style={styles.revert}/>) : null}
        <Button brand title={strings.save} isFetching={isFetching} disabled={!amount || !date} onPress={onPressSave}/>
      </KeyboardAvoidingContainerNew>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    scrollContent: {
        flexGrow: 1,
        paddingTop: rem(8),
    },
    revert: {
        alignSelf: 'center',
        marginBottom: rem(16),
    },
}));
export default AdjustPredictionScreen;
